import Image from 'next/image';
import Slider from 'rc-slider';
import { Fragment, useEffect, useState } from 'react';
import {
  useClearRefinements,
  useNumericMenu,
  useRange,
  useStats,
  useToggleRefinement
} from 'react-instantsearch';

import { staticMediaStoreBaseURL } from '@/config/common';
import DISCOUNT_ITEMS from '@/config/searchFilters';

const ProductFilterModal = ({
  openTab,
  priceRange,
  setCloseModal,
  setOpenTab,
  updateCurrentUrl
}) => {
  const { nbHits } = useStats();

  const { value: instantPriceValue, refine: refineInstantPrice } =
    useToggleRefinement({ attribute: 'isInstantPrice', on: true });

  const {
    range,
    start,
    refine: refinePrice,
    canRefine: canRefinePrice
  } = useRange({ attribute: 'price.sortPrice' });

  const { items: discountOptions, refine: refineDiscount } = useNumericMenu({
    attribute: 'discount',
    items: DISCOUNT_ITEMS
  });

  const { refine: clearDiscountFilter } = useClearRefinements({
    includedAttributes: ['discount']
  });

  const [currentMin, currentMax] = start;
  const committedMin = Number.isFinite(currentMin)
    ? currentMin
    : (range.min ?? 0);
  const committedMax = Number.isFinite(currentMax)
    ? currentMax
    : (range.max ?? 0);

  const [sliderValue, setSliderValue] = useState([committedMin, committedMax]);
  const [displayMin, displayMax] = sliderValue;

  useEffect(() => {
    setSliderValue([committedMin, committedMax]);
  }, [committedMin, committedMax]);

  const showPriceSlider =
    (canRefinePrice || Number.isFinite(currentMin)) &&
    (priceRange?.max_price?.value > 0 || (range.max && range.max > 0));

  const handleApplyFilters = () => {
    updateCurrentUrl();
    setOpenTab(1);
    setCloseModal(false);
  };

  return (
    <Fragment>
      <div className='flex justify-between'>
        <div className='w-1/3 bg-lightpink h-customscreen min-w-30'>
          <ul
            className='bg-lightpink'
            role='tablist'
          >
            <li
              className={`border-b border-white h-11 flex justify-start items-center ${
                openTab === 1 ? 'bg-white' : 'bg-lightpink'
              }`}
            >
              <label
                className={`px-4 text-xs leading-4 text-nero w-full ${
                  openTab === 1 ? 'font-semibold' : 'font-normal'
                }`}
                onClick={() => setOpenTab(1)}
                data-toggle='tab'
                href='#price'
                role='tablist'
              >
                Price
              </label>
            </li>
            <li
              className={`border-white h-11 flex justify-start items-center ${
                openTab === 2 ? 'bg-white' : 'bg-lightpink'
              }`}
            >
              <label
                className={`px-4 text-sm leading-4 text-nero w-full ${
                  openTab === 2 ? 'font-semibold' : 'font-medium'
                }`}
                onClick={() => setOpenTab(2)}
                data-toggle='tab'
                href='#discount'
                role='tablist'
              >
                Discount
              </label>
            </li>
          </ul>
        </div>

        <div className='w-2/3'>
          <div className='tab-content tab-space font-Montserrat'>
            <div
              className={openTab === 1 ? 'block' : 'hidden'}
              id='price'
            >
              <ul className='instantprice'>
                <li className='mx-4 h-11 flex justify-start items-center border-b border-gray'>
                  <label>
                    <div className='flex items-center'>
                      <input
                        type='checkbox'
                        name='Instant Price Products'
                        className='rounded-sm box-border border-gray border-1 text-nero focus:border-nero focus:ring-nero w-4 h-4'
                        onChange={() => refineInstantPrice(instantPriceValue)}
                        checked={instantPriceValue.isRefined}
                      />
                      <p className='text-nero font-normal text-xs leading-5 capitalize ml-3'>
                        {' '}
                        Instant Price Products Only{' '}
                      </p>
                    </div>
                  </label>
                </li>
              </ul>
              {showPriceSlider && (
                <div className='price mx-5 block'>
                  <div className='block mt-5'>
                    <p className='text-nero font-normal text-xs leading-5 capitalize'>
                      {' '}
                      Selected price range{' '}
                    </p>
                    <p className='text-nero text-sm leading-17px font-semibold mt-2 mb-6'>
                      AED {displayMin} - AED {displayMax}
                    </p>
                    <div className='custom-slider max-w-44 mr-auto relative'>
                      <Slider
                        range
                        min={range.min ?? 0}
                        max={range.max ?? 0}
                        value={[displayMin, displayMax]}
                        onChange={(value) => setSliderValue(value)}
                        onChangeComplete={(value) => refinePrice(value)}
                        allowCross={false}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className={openTab === 2 ? 'block' : 'hidden'}
              id='discount'
            >
              <ul className='discount'>
                {discountOptions.map((item) => (
                  <li
                    key={item.value}
                    className='mx-4 h-11 flex justify-start items-center border-b border-gray'
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.isRefined) {
                        clearDiscountFilter();
                      } else {
                        refineDiscount(item.value);
                      }
                    }}
                  >
                    <label>
                      <div className='flex items-center'>
                        <Image
                          src={`${staticMediaStoreBaseURL}${
                            item.isRefined
                              ? '/icons/checkbox-orange.svg'
                              : '/icons/checkbox-grey.svg'
                          }`}
                          height={16}
                          width={16}
                          alt={item.label}
                        />
                        <p className='ms-2 basic:ms-4.5 text-nero font-normal text-xs basic:text-sm leading-5 capitalize'>
                          {' '}
                          {item.label}{' '}
                        </p>
                        <input
                          type='checkbox'
                          className='hidden'
                          readOnly
                          checked={item.isRefined}
                        />
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className='fixed bottom-0 left-0 z-120 w-full h-18'>
            <div className='flex justify-between items-center px-5 py-4 border-t border-gray bg-white box-border'>
              <div className='w-1/2'>
                <span className='text-xs font-medium leading-4 text-nero mb-1 underline block'>
                  {' '}
                  {nbHits}{' '}
                </span>
                <p className='text-xs font-medium leading-4 text-dim-gray'>
                  {' '}
                  Products found{' '}
                </p>
              </div>
              <div
                className='w-1/2 text-right'
                onClick={handleApplyFilters}
              >
                <button
                  type='submit'
                  className='h-10 max-w-35 w-full rounded-md font-Montserrat text-base leading-5 text-white font-medium bg-brand-gradient'
                >
                  {' '}
                  Apply{' '}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ProductFilterModal;
